import { useEffect, useRef, useState } from "react";

import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  MenuItem,
  Stack,
  styled,
  TextField,
  Toolbar,
  Tooltip,
} from "@mui/material";
import {
  Add,
  Cancel,
  CloudUpload,
  Edit,
  LinkOff,
  Refresh,
  Save,
} from "@mui/icons-material";
import {
  DataGrid,
  GridActionsCellItem,
  GridEditSingleSelectCell,
  GridRowEditStopReasons,
  GridRowModes,
  useGridApiContext,
} from "@mui/x-data-grid";

import { useLoaderData } from "react-router-dom";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import { CLASSIFICATION_URL } from "../../helpers/variables";
import confirm from "../../Components/Confirm.tsx";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const DEFAULT_ROW_SELECTION_MODAL = {
  type: "include",
  ids: new Set(),
};

const LineItems = () => {
  const { PMSList } = useLoaderData();

  const apiRef = useRef();
  const [rowModesModel, setRowModesModel] = useState({});
  const [selectedRows, setSelectedRows] = useState(DEFAULT_ROW_SELECTION_MODAL);

  const [PMS, setPMS] = useState("");
  const [reportNames, setReportNames] = useState([]);
  const [labels, setLabels] = useState([]);
  const [lineItems, setLineItems] = useState([]);

  const [editable, setEditable] = useState(true);
  const [loading, setLoading] = useState(false);

  const isRowSelect = selectedRows.ids.size !== 0;

  const subDeptList = [
    "Room Revenue",
    "F & B Revenue",
    "Gift Shop Revenue",
    "Miscellaneous Revenue",
    "Non-Revenue",
    "Taxes Collected",
    "Cash Receipts",
    "Credit Card Receipts",
    "Other Receipts",
    "AR_Ledger",
    "Verification",
  ]

  const getLabel = (labelID) => labels.find((i) => i._id === labelID);
  const isLineItemInserted = lineItems.some((item) => item.isNew);

  useEffect(() => {
    getInitialData();
  }, []);

  useEffect(() => {
    if (isLineItemInserted) {
      apiRef.current.scrollToIndexes({ rowIndex: 0 });
    }
  }, [isLineItemInserted]);

  async function getInitialData() {
    setLoading(true);
    await getLabelList();
    setLoading(false);
  }

  async function getLabelList() {
    let res = await fetch(`${CLASSIFICATION_URL}/labels_list`);
    let data = await res.json();
    setLabels(data);
  }

  async function getLineItems(v) {
    if (v === "") return;
    setPMS(v);
    setLoading(true);
    let res = await fetch(`${CLASSIFICATION_URL}/get_line_items?pms=${v}`);
    let data = await res.json();
    setLineItems(data["line_items"]);
    setReportNames(data["report_names"]);
    setLoading(false);
  }

  async function deleteLineItems() {
    let userConfirmation = await confirm({
      message: `Are you sure you want to delete this line item`,
    });
    if (!userConfirmation) return;

    let ids = Array.from(selectedRows.ids);
    setLoading(true);
    let res = await fetch(`${CLASSIFICATION_URL}/delete_line_items`, {
      method: "DELETE",
      headers: {
        "Content-type": "application/json",
      },
      body: JSON.stringify(ids),
    });
    setLoading(false);

    if (res.status !== 204) return;

    setLineItems(lineItems.filter((i) => !ids.includes(i._id)));
  }

  async function updateLineItem(labelID, data) {
    setLoading(true);
    let res = await fetch(
      `${CLASSIFICATION_URL}/update_line_item?line_item_id=${labelID}`,
      {
        method: "PATCH",
        headers: {
          "Content-type": "application/json",
        },
        body: JSON.stringify(data),
      }
    );
    setLoading(false);
    return res;
  }

  async function addLineItem(data) {
    setLoading(true);
    let res = await fetch(`${CLASSIFICATION_URL}/add_line_item`, {
      method: "POST",
      headers: {
        "Content-type": "application/json",
      },
      body: JSON.stringify(data),
    });
    setLoading(false);
    return res;
  }

  async function downLoadSampleTemplate() {
    let res = await fetch(`${CLASSIFICATION_URL}/sample_template`, {
      method: "POST",
      headers: {
        "Content-type": "application/json",
      },
      body: JSON.stringify(reportNames),
    });
    let blob = await res.blob();
    saveAs(blob, `${PMS} sample template.xlsx`);
  }

  async function uploadSampleTemplate(e) {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);
    let res = await fetch(`${CLASSIFICATION_URL}/template_import?pms=${PMS}`, {
      method: "POST",
      body: formData,
    });
    setLoading(false);

    if (res.status !== 204) return;
    getLineItems(PMS);
  }

  const handleSaveClick = (id) => async () => {
    let userConfirmation = await confirm({
      message: `Are you sure you want to update this line item`,
    });
    if (!userConfirmation) return;
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View },
    });
  };

  const processRowUpdate = async (newRow, oldRow) => {
    let { _id, isNew, sub_dept, ...others } = newRow;

    if (isNew) {
      let res = await addLineItem({ ...others });
      if (res.status !== 200) return oldRow;
      _id = await res.text();
    } else {
      let res = await updateLineItem(_id, { ...others });
      if (res.status !== 204) return oldRow;
    }

    const updatedRow = { ...newRow, _id, isNew: false };
    setLineItems(lineItems.map((i) => (i._id === newRow._id ? updatedRow : i)));
    return updatedRow;
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = lineItems.find((i) => i._id === id);
    if (editedRow.isNew) {
      setLineItems(lineItems.filter((i) => i._id !== id));
    }
  };

  function insertLineItem() {
    const id = "new_line";
    setLineItems((oldRows) => [
      {
        _id: id,
        line_item: "",
        category: "",
        sub_category: "",
        pms: PMS.toLowerCase().replace(/\s+/g, ""),
        report_name: reportNames.length === 1 ? reportNames[0] : "",
        label_id: null,
        status: 1,
        isNew: true,
      },
      ...oldRows,
    ]);
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: {
        mode: GridRowModes.Edit,
        fieldToFocus: reportNames.length === 1 ? "line_item" : "report_name",
      },
    }));
    apiRef.current.setPage(0);
  }

  function CustomSubDeptEditComponent(props) {
    const apiRef = useGridApiContext();

    const handleValueChange = async () => {
      await apiRef.current.setEditCellValue({
        id: props.id,
        field: "label_id",
        value: "",
      });
    };

    return (
      <GridEditSingleSelectCell onValueChange={handleValueChange} {...props} />
    );
  }

  return (
    <Stack height={"100%"}>
      <Toolbar sx={{ gap: 2 }}>
        <TextField
          select
          value={PMS}
          onChange={(e) => getLineItems(e.target.value)}
          size="small"
          sx={{ minWidth: 280 }}
          label="PMS"
          slotProps={{
            input: {
              endAdornment: PMS !== "" && (
                <Tooltip arrow title="Refresh Line Items">
                  <IconButton
                    onClick={() => {
                      getLineItems(PMS);
                      setRowModesModel({});
                      setSelectedRows(DEFAULT_ROW_SELECTION_MODAL);
                    }}
                    size="small"
                    sx={{ mr: 2 }}
                  >
                    <Refresh fontSize="small" />
                  </IconButton>
                </Tooltip>
              ),
            },
          }}
        >
          {PMSList.map((item, index) => (
            <MenuItem key={index} value={item}>
              {item}
            </MenuItem>
          ))}
        </TextField>
        {PMS !== "" && (
          <>
            <Button
              startIcon={<Add />}
              onClick={insertLineItem}
              disabled={isLineItemInserted}
              variant="outlined"
            >
              Add Line Item
            </Button>

            <Button color="success" variant="outlined" onClick={downLoadSampleTemplate}>
              Sample template
            </Button>
            <Button
              component="label"
              role={undefined}
              variant="contained"
              tabIndex={-1}
              startIcon={<CloudUpload />}
            >
              Upload files
              <VisuallyHiddenInput
                type="file"
                accept=".xlsx, .csv"
                onChange={(e) => uploadSampleTemplate(e)}
              />
            </Button>
          </>
        )}
      </Toolbar>

      {isRowSelect && (
        <Box minHeight={52} pl={3}>
          <Button variant="contained" color="error" onClick={deleteLineItems}>
            Delete
          </Button>
        </Box>
      )}

      <DataGrid
        apiRef={apiRef}
        rows={lineItems}
        getRowId={(row) => row._id}
        columns={[
          {
            field: "report_name",
            headerName: "Report Name",
            flex: 1,
            editable,
            type: "singleSelect",
            valueOptions: reportNames,
          },
          {
            field: "line_item",
            headerName: "Line Item",
            flex: 1,
            editable,
          },
          {
            field: "category",
            headerName: "Category",
            flex: 1,
            editable,
          },
          {
            field: "sub_category",
            headerName: "Sub Category",
            flex: 1,
            editable,
          },
          {
            field: "sub_dept",
            headerName: "Sub Department",
            flex: 1,
            editable,
            valueGetter: (v, r, c) => getLabel(r.label_id)?.sub_dept || "",
            type: "singleSelect",
            valueOptions: subDeptList,
            renderEditCell: (params) => (
              <CustomSubDeptEditComponent {...params} />
            ),
          },
          {
            field: "label_id",
            headerName: "Label",
            flex: 1,
            editable,
            type: "singleSelect",
            renderCell: ({ value }) => getLabel(value)?.label || "",
            valueOptions: ({ row }) =>
              !row
                ? labels
                : row.sub_dept
                ? labels.filter((i) => i.sub_dept === row.sub_dept)
                : undefined,
            getOptionValue: (value) => value._id,
            getOptionLabel: (option) => option.label,

            valueFormatter: (v, r, c) => getLabel(v)?.label,
          },
          {
            field: "actions",
            type: "actions",
            headerName: "Actions",
            width: 100,
            getActions: ({ id, row }) => {
              const isInEditMode =
                rowModesModel[id]?.mode === GridRowModes.Edit;

              if (isInEditMode) {
                return [
                  <GridActionsCellItem
                    icon={<Save />}
                    label="Save"
                    onClick={handleSaveClick(id)}
                  />,
                  <GridActionsCellItem
                    icon={<Cancel />}
                    label="Cancel"
                    onClick={handleCancelClick(id)}
                  />,
                ];
              }

              return [
                <GridActionsCellItem
                  icon={<Edit />}
                  label="Edit"
                  onClick={() =>
                    setRowModesModel({
                      ...rowModesModel,
                      [id]: { mode: GridRowModes.Edit },
                    })
                  }
                />,
              ];
            },
          },
        ]}
        rowModesModel={rowModesModel}
        onRowModesModelChange={(newRowModesModel) =>
          setRowModesModel(newRowModesModel)
        }
        onRowEditStop={(params, event) => {
          if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = true;
          }
        }}
        processRowUpdate={processRowUpdate}
        loading={loading}
        editMode="row"
        showToolbar={!isRowSelect}
        checkboxSelection
        disableRowSelectionOnClick
        onRowSelectionModelChange={(r) => setSelectedRows(r)}
        rowSelectionModel={selectedRows}
      />
    </Stack>
  );
};

export default LineItems;
